import { usePulseTracker } from '@cineamo/pulse/src/react/PulseProvider';
import { trackedEntity } from '@cineamo/pulse/src/subject';
import { useLocale, useTranslations } from 'next-intl';
import React, { CSSProperties, useMemo, useState } from 'react';

import { ShowingCinemasStackedLogos } from '@/app/[locale]/(default-layout)/program/_components/ShowingCinemasStackedLogos';

import AppDiscountPopup from '@/components/AppDiscountPopup';
import { NoOnlineTicketUrlModal } from '@/components/NoOnlineTicketUrlModal';
import { ShowingInfoModal } from '@/components/showing-details/ShowingInfoModal';
import TicketLink, { useTicketUrl } from '@/components/ticket-shape/TicketLink';
import TicketShape from '@/components/ticket-shape/TicketShape';

import { ShowingDto, ShowingGroupedByDateTimeDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { ID_KINO_EHINGEN } from '@cineamo/api/constants/cinema-ids';
import { Client } from '@cineamo/api/constants/Client.enum';
import { PurchaseReasonEnum } from '@cineamo/api/constants/PurchaseReason.enum';

import { onTicketButtonClick } from '@/helpers/ticket-purchase-intent-helper';
import { isDatetimePast } from '@/helpers/time-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';
import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { ISO_639_3_LOCALE, convertIso6393ToIso6391 } from '@cineamo/ui/src/helpers/locale-helper';
import {
    getHoursUntilShowing,
    getShowingLanguages,
    getShowingPropertiesDimension,
    getShowingPropertiesLanguage,
    getShowingTechnologies
} from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { Icon3DGlasses16 } from '@cineamo/ui/src/icons/ic-3dGlasses';
import { IconInfo16 } from '@cineamo/ui/src/icons/ic-info';
import { IconStarSolid16 } from '@cineamo/ui/src/icons/ic-starSolid';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useShowingTags } from '@/providers/ShowingTagProvider';
import { usePageName } from '@/utils/tracking-helper';

export type TicketButtonGroupedProps = {
    className?: string;
    showCinemaLogos?: boolean;
    showCinemaName?: boolean;
    showDateAndTime?: boolean;
    showTags?: boolean;
    content?: CachedContentData;
    style?: CSSProperties;
    showingTagStyle?: CSSProperties;
} & (
    | {
          groupedShowing?: never;
          showing: ShowingDto;
      }
    | {
          groupedShowing: ShowingGroupedByDateTimeDto;
          showing?: never;
      }
);

export enum TicketItemStyle {
    Default,
    Light
}

function TicketButtonGrouped(props: TicketButtonGroupedProps): React.JSX.Element {
    const { className, showCinemaLogos, showCinemaName, showDateAndTime, showTags, content, showingTagStyle } = props;

    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();
    const { track: pulseTrack } = usePulseTracker();

    const t = useTranslations();
    const locale = useLocale();
    const currentPageName = usePageName();

    const { getShowingTagById } = useShowingTags();

    const groupedShowings = useMemo(() => {
        return props.groupedShowing?.showings ? props.groupedShowing?.showings : props.showing ? [props.showing] : [];
    }, [props.groupedShowing?.showings, props.showing]);
    const isSingleShowing = groupedShowings.length === 1;
    const showing = props.showing || groupedShowings?.[0];
    const showingId = showing.id;
    const showingName = showing.name || content?.title;

    // De-orphans the canonical showing detail page: a real, crawlable <Link href> to
    // /program/[contentId]/[showingId], rendered outside the ticket anchor below (nesting an <a>
    // inside TicketLink's own <a>/<button> would be invalid HTML and break hydration).
    const showingDetailHref: HrefOrHrefWithParams<'/program/[contentId]/[showingId]'> = {
        pathname: '/program/[contentId]/[showingId]',
        params: {
            contentId: showing.contentId,
            showingId: showing.id
        }
    };

    const [noTicketUrlIsOpen, setNoTicketUrlIsOpen] = useState<boolean>(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
    const [isAppDiscountPopupOpen, setIsAppDiscountPopupOpen] = useState<boolean>(false);
    const [pendingTicketSource, setPendingTicketSource] = useState<string | undefined>(undefined);

    const cinema = domainConfiguration.cinema;
    const showAppDiscountPopup =
        cinema?.id === ID_KINO_EHINGEN &&
        cinema?._embedded?.cinemaConfiguration?.onlineTicketing?.ios === true &&
        cinema?._embedded?.cinemaConfiguration?.onlineTicketing?.android === true;
    const hasOwnWebsiteTerminal =
        cinema?._embedded?.cinemaConfiguration?.ticketSystemTerminals?.includes(Client.CinemaWebsite) === true;
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const weekday = format.dateTime.formatWeekday(showing.startDatetime, 'short').replace('.', '');
    const date = format.dateTime.formatDayMonth(showing.startDatetime);
    const time = format.dateTime.formatDate_HHmm24(showing.startDatetime);

    const isTicketClickDisabled = isDatetimePast(showing.startDatetime, true);

    const { ticketUrl, hasTicketUrl, isExternal } = useTicketUrl(showing);

    const highlightColor = domainConfiguration.style.secondaryColor ?? domainConfiguration.style.primaryColor;

    const ticketItemBgColorHex = props.style?.backgroundColor ?? domainConfiguration.style.surfaceColor;
    const ticketItemBgOpacity: number = 1;
    const ticketItemUnavailableBgColorHex = props.style?.backgroundColor ?? domainConfiguration.style.surfaceColor;
    const ticketItemUnavailableBgOpacity: number = 1;
    const ticketItemTimeColor = props.style?.color ?? domainConfiguration.style.onSurfaceColor;
    const ticketItemUnavailableTextColor: string = 'opacity-60';

    const showingTags = useMemo(() => {
        if (!showTags) {
            return [];
        }

        return (
            showing.showingTagIds?.map((tagId) => {
                return getShowingTagById(tagId) ?? null;
            }) ?? []
        );
    }, [showTags, showing.showingTagIds, getShowingTagById]);

    function handleModalTicketClick(source: string) {
        if (!isSingleShowing) return;

        pulseTrack({
            ...trackedEntity({ movieId: content?.movieId, contentId: showing.contentId }),
            event_type: 'ticket_purchase_intent',
            context: {
                showing_id: showingId,
                ...(showing.showingTagIds?.length && { showing_tag_ids: showing.showingTagIds }),
                page: currentPageName
            }
        });

        if (!hasTicketUrl) {
            setNoTicketUrlIsOpen(true);
            return;
        }
        if (isTicketClickDisabled) {
            return;
        }

        onTicketButtonClick(showingId, PurchaseReasonEnum.CINEMA_WEBSITE, showing.contentId);
        track('Ticket Button Clicked', {
            page: currentPageName + ` | ${source}`,
            showingId: showingId,
            showingName: showingName ?? '',
            contentId: showing.contentId,
            contentName: content?.name || 'N/A',
            hoursUntilShowing: getHoursUntilShowing(showing.startDatetime) || 'N/A'
        });
    }

    function showInfoModal(e: React.SyntheticEvent) {
        pulseTrack({
            ...trackedEntity({ movieId: content?.movieId, contentId: showing.contentId }),
            event_type: 'showing_details_view',
            context: { showing_id: showingId }
        });

        if (isTicketClickDisabled) {
            return;
        }

        e.stopPropagation();
        e.preventDefault();
        track('Showing Info Modal Opened', {
            source: currentPageName,
            showingId: showingId,
            showingName: showingName ?? '',
            contentId: showing.contentId,
            contentName: content?.name || 'N/A'
        });
        setIsInfoModalOpen(true);
    }

    const showingDimension = useMemo(() => {
        // The shared helper returns null for a 2D showing (the web app hides it); this website
        // labels it explicitly.
        const showingPropertiesDimension = getShowingPropertiesDimension(showing) ?? '2D';
        const isHighlighted = !isTicketClickDisabled;
        if (showing.isThreeDimensional === true) {
            return (
                <span
                    className={cn('inline-flex flex-row items-center space-x-4')}
                    style={
                        domainConfiguration
                            ? {
                                  color: isHighlighted ? highlightColor : undefined
                              }
                            : undefined
                    }>
                    <Icon3DGlasses16 className="flex-none" />
                    <span>{showingPropertiesDimension}</span>
                </span>
            );
        }
        return <span className="opacity-70">{showingPropertiesDimension}</span>;
    }, [showing, domainConfiguration, highlightColor, isTicketClickDisabled]);

    const showingLanguage = useMemo(() => {
        const showingPropertiesLanguage = getShowingPropertiesLanguage(showing, locale);
        const isHighlighted =
            (showing.isOriginalLanguage && locale !== convertIso6393ToIso6391(showing.language as ISO_639_3_LOCALE)) ||
            showing.isSubtitled;
        return showingPropertiesLanguage.map((property) => (
            <span
                key={`ShowingProperty#${property}`}
                style={{
                    opacity: isTicketClickDisabled ? undefined : isHighlighted ? 1.0 : 0.7,
                    color: isHighlighted && !isTicketClickDisabled ? highlightColor : undefined
                }}>
                {property}
            </span>
        ));
    }, [showing, locale, isTicketClickDisabled, highlightColor]);

    const showingFeatures = useMemo(
        () =>
            getShowingTechnologies(showing).map((technology: string) => (
                <span
                    className="opacity-70 whitespace-pre"
                    key={showing.id + technology}>
                    {t(`showtimes.showing-info.technology.${technology}`)}
                </span>
            )),
        [showing, t]
    );

    const [now] = useState(() => Date.now());

    const shouldHighlight = showing.highlightAfterDatetime && new Date(showing.highlightAfterDatetime).getTime() < now;
    const shouldShowSpecialsElement = shouldHighlight || showingTags.length > 0;

    const specialsElement = shouldShowSpecialsElement && (
        <span
            className={cn(
                'text-body-bold-10 flex items-center flex-row gap-4 min-w-0 max-w-full pointer-events-auto',
                isTicketClickDisabled && 'opacity-60'
            )}
            style={{
                color: showingTagStyle?.color ?? ticketItemTimeColor
            }}>
            {shouldHighlight && (
                <span className="inline-flex items-center gap-4 align-middle shrink-0">
                    <IconStarSolid16 className="flex-none" />
                    <span className="capitalize">
                        {t('showing-info.highlight')}
                        {showingTags.length > 0 && ', '}
                    </span>
                </span>
            )}
            <span className="min-w-0 truncate">{showingTags.map((tag) => tag?.name).join(', ')}</span>
        </span>
    );

    const showingTimeElement = (
        <div
            style={{
                color: ticketItemTimeColor
            }}
            className={cn('flex row justify-between', showDateAndTime ? 'text-bold' : 'text-headline-18')}>
            <div className="flex row items-center space-x-1.5">
                {showDateAndTime && (
                    <div className="flex">
                        <span className="whitespace-nowrap">
                            {weekday}, {date}
                        </span>
                    </div>
                )}
                <span className="whitespace-nowrap">{time}</span>
            </div>
            <div className="flex row items-center space-x-1.5">
                {/* role="button" span, NOT a <button>: this sits inside TicketLink's <a>/<button>, and a
                    button/anchor may not contain a nested button (invalid HTML → hydration error).
                    showInfoModal stops propagation/prevents default so the surrounding ticket action
                    doesn't fire too. */}
                <span
                    role="button"
                    tabIndex={0}
                    aria-label="Show info"
                    className="inline-flex cursor-pointer"
                    onClick={showInfoModal}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            showInfoModal(e);
                        }
                    }}>
                    <IconInfo16 className="opacity-60 shrink-0" />
                </span>
            </div>
        </div>
    );

    const hint = getShowingLanguages(showing, locale, {
        languageFormat: 'long',
        languages: t.raw('languages')
    });

    const showingDimensionElement = (
        <div
            className="w-full flex mt-6"
            title={[
                hint.audioLanguage ? `${t('showing-info.audio')}: ${hint.audioLanguage}` : null,
                hint.subtitleLanguage ? `${t('showing-info.subtitle')}: ${hint.subtitleLanguage}` : null
            ]
                .filter((it) => (it?.length ?? 0) > 0)
                .join('\n')}>
            <DotSeparatedLabel
                className={cn('flex overflow-hidden text-ellipsis', 'text-body-bold-10')}
                classNameLabel="flex"
                labels={[showingDimension, ...showingLanguage, ...showingFeatures].filter((label) => !!label)}
            />
        </div>
    );

    const { getCinemaRoomById } = useCinemaRooms();
    const cinemaRoom = getCinemaRoomById(showing.cinemaRoomId);

    const showingLocationElement = (
        <div className="text-body-14 truncate mt-4">
            {showCinemaName && (
                <>
                    {showing._embedded?.cinema?.shortName} <span className="opacity-60">|</span>{' '}
                </>
            )}
            {cinemaRoom?.name}
        </div>
    );

    const ticketShape = (
        <TicketShape
            className={cn(
                'w-full overflow-hidden',
                isTicketClickDisabled ? 'cursor-not-allowed' : 'cursor-pointer clickable-element'
            )}
            config={{
                fillColorHex: isTicketClickDisabled ? ticketItemUnavailableBgColorHex : ticketItemBgColorHex,
                fillColorOpacity: isTicketClickDisabled ? ticketItemUnavailableBgOpacity : ticketItemBgOpacity,
                cornerRadius: 8,
                footerBackgroundColor:
                    showingTagStyle?.backgroundColor ?? withOpacity(domainConfiguration?.style?.onSurfaceColor, 0.1)
            }}
            footer={specialsElement || undefined}>
            <div
                className={cn(
                    'relative w-full h-full flex flex-row',
                    isTicketClickDisabled && ticketItemUnavailableTextColor,
                    showCinemaLogos ? 'px-8' : showDateAndTime ? 'pl-10 pr-20' : 'px-16'
                )}>
                {showCinemaLogos && <ShowingCinemasStackedLogos showings={groupedShowings} />}
                <div
                    className="flex flex-col w-full"
                    style={{
                        color: ticketItemTimeColor
                    }}>
                    {showingTimeElement}
                    {showingLocationElement}
                    {showingDimensionElement}
                </div>
            </div>
        </TicketShape>
    );

    const onShare = () => {
        track('Share Showing Detail Link Clicked', {
            source: currentPageName,
            showingId: showingId,
            showingName: showingName ?? '',
            contentId: showing.contentId
        });
        pulseTrack({
            ...trackedEntity({ movieId: content?.movieId, contentId: showing.contentId }),
            event_type: 'share',
            context: { showing_id: showingId }
        });
    };

    return (
        <div className={cn('relative', className ?? 'h-full')}>
            {isInfoModalOpen && (
                <ShowingInfoModal
                    showing={showing}
                    content={content}
                    modalIsOpen={isInfoModalOpen}
                    setModalIsOpen={setIsInfoModalOpen}
                    specials={showingTags}
                    onClose={() => setIsInfoModalOpen(false)}
                    onShare={onShare}
                    onSubmit={() => {
                        if (showAppDiscountPopup) {
                            setPendingTicketSource('Showing Info Modal');
                            setIsInfoModalOpen(false);
                            setIsAppDiscountPopupOpen(true);
                            return;
                        }
                        if (hasTicketUrl) {
                            window.open(ticketUrl, isExternal ? '_blank' : '_self');
                        }
                        handleModalTicketClick('Showing Info Modal');
                    }}
                />
            )}

            {noTicketUrlIsOpen && (
                <NoOnlineTicketUrlModal
                    modalIsOpen={noTicketUrlIsOpen}
                    setModalIsOpen={setNoTicketUrlIsOpen}
                />
            )}

            {isAppDiscountPopupOpen && (
                <AppDiscountPopup
                    isOpen={isAppDiscountPopupOpen}
                    isPriceClaimOmitted={hasOwnWebsiteTerminal}
                    onClose={() => setIsAppDiscountPopupOpen(false)}
                    onContinue={() => {
                        setIsAppDiscountPopupOpen(false);
                        handleModalTicketClick(pendingTicketSource ?? '');
                        if (hasTicketUrl) {
                            window.open(ticketUrl, isExternal ? '_blank' : '_self');
                        }
                    }}
                />
            )}

            {isSingleShowing ? (
                <TicketLink
                    showing={showing}
                    content={content}
                    className={cn(
                        'h-full flex flex-row flex-none pointer-events-auto',
                        !isTicketClickDisabled && hasTicketUrl && 'clickable-element'
                    )}
                    onClick={(e) => {
                        if (showAppDiscountPopup && !isTicketClickDisabled) {
                            e.preventDefault();
                            setPendingTicketSource(undefined);
                            setIsAppDiscountPopupOpen(true);
                        }
                    }}>
                    {ticketShape}
                </TicketLink>
            ) : (
                <div
                    className={cn('h-full flex items-center')}
                    onClick={() => {
                        // TODO: Open Showtimes & Tickets Modal
                    }}>
                    {ticketShape}
                </div>
            )}

            {isSingleShowing && (
                <Link
                    href={showingDetailHref}
                    prefetch={false}
                    className={cn(
                        'sr-only focus:not-sr-only focus:absolute focus:z-10 focus:top-4 focus:left-4',
                        'focus:rounded-4 focus:px-8 focus:py-4 focus:text-body-bold-10 focus:pointer-events-auto'
                    )}
                    style={{
                        backgroundColor: ticketItemBgColorHex,
                        color: ticketItemTimeColor
                    }}>
                    {t('showing-info.details', { title: showingName ?? '' })}
                </Link>
            )}
        </div>
    );
}

export default TicketButtonGrouped;
